import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Linkedin, Link2, ArrowRight, Loader2, Check, Share2 } from 'lucide-react';
import axios from 'axios';
import { useAuth, API } from '../App';
import { toast } from 'sonner';

const TAGLINES = {
  A_green: 'Ready \u2022 Capable \u2022 Go',
  A_yellow: 'Ready \u2022 Conditions Apply \u2022 Go Thoughtfully',
  B: 'Talented \u2022 Structured \u2022 Built to Lead',
  C: 'Capable \u2022 Developing \u2022 Ready-with-Support',
};

const PATHWAY_NAMES = {
  A: 'Founders Track',
  B: 'Employment Track',
  C: 'Mindset Discovery',
};

const Badge = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [badgeId, setBadgeId] = useState('');
  const [copied, setCopied] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const res = await axios.get(`${API}/assessment/stage1/results`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setResults(res.data.results);
        // Generate badge ID
        const id = `SPR-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).substr(2, 4).toUpperCase()}`;
        setBadgeId(id);
      } catch (e) {
        navigate('/results');
      }
      setLoading(false);
    };
    fetchResults();
  }, [token, navigate]);

  const pathway = results?.pathway || 'C';
  const taglineKey = pathway === 'A' ? (results?.light_colour === 'green' ? 'A_green' : 'A_yellow') : pathway;
  const tagline = TAGLINES[taglineKey] || TAGLINES.C;
  const pathwayName = PATHWAY_NAMES[pathway] || 'Mindset Discovery';
  const candidateName = user?.name || 'Candidate';
  const completedDate = new Date().toLocaleDateString('en-NZ', { day: 'numeric', month: 'long', year: 'numeric' });

  const lightColours = {
    green: { bg: '#166534', accent: '#22c55e' },
    yellow: { bg: '#854d0e', accent: '#eab308' },
    orange: { bg: '#9a3412', accent: '#f97316' },
    red: { bg: '#991b1b', accent: '#ef4444' },
  };
  const lc = lightColours[results?.light_colour] || lightColours.yellow;

  // Draw badge on canvas for PNG export
  const drawBadge = (ctx, w, h) => {
    // Background
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(0, 0, w, h);

    // Yellow accent bar top
    ctx.fillStyle = '#F6ADBC';
    ctx.fillRect(0, 0, w, 6);

    // Header
    ctx.fillStyle = '#F6ADBC';
    ctx.font = 'bold 28px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('SPRING.', w / 2, 60);

    ctx.fillStyle = '#ffffff80';
    ctx.font = '14px Arial';
    ctx.fillText('STAGE 1: ASSESSMENT COMPLETE', w / 2, 85);

    // Divider
    ctx.strokeStyle = '#ffffff20';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(80, 105);
    ctx.lineTo(w - 80, 105);
    ctx.stroke();

    // Name
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 36px Arial';
    ctx.fillText(candidateName, w / 2, 160);

    ctx.fillStyle = '#ffffffb0';
    ctx.font = '16px Arial';
    ctx.fillText('has completed the SPRING Stage 1: Assessment', w / 2, 195);

    // Score circle
    const cx = w / 2;
    const cy = 280;
    const r = 50;

    // Outer ring
    ctx.beginPath();
    ctx.arc(cx, cy, r + 4, 0, Math.PI * 2);
    ctx.fillStyle = lc.accent + '30';
    ctx.fill();

    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = '#1a1a1a';
    ctx.fill();
    ctx.strokeStyle = lc.accent;
    ctx.lineWidth = 3;
    ctx.stroke();

    // Score number
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 32px Arial';
    ctx.fillText(`${results?.total_score || 0}`, cx, cy + 8);
    ctx.fillStyle = '#ffffff70';
    ctx.font = '12px Arial';
    ctx.fillText('/ 50', cx, cy + 26);

    // Pathway
    ctx.fillStyle = lc.accent;
    ctx.font = 'bold 18px Arial';
    ctx.fillText(`Pathway ${pathway}: ${pathwayName}`, w / 2, 370);

    // Tagline
    ctx.fillStyle = '#ffffffa0';
    ctx.font = 'italic 14px Arial';
    ctx.fillText(tagline, w / 2, 400);

    // Bottom divider
    ctx.strokeStyle = '#ffffff20';
    ctx.beginPath();
    ctx.moveTo(80, 430);
    ctx.lineTo(w - 80, 430);
    ctx.stroke();

    // Footer
    ctx.fillStyle = '#ffffff60';
    ctx.font = '12px Arial';
    ctx.fillText(`Completed: ${completedDate}`, w / 2, 460);
    ctx.fillText(`Badge ID: ${badgeId}`, w / 2, 480);

    // SPRING branding
    ctx.fillStyle = '#F6ADBC';
    ctx.font = '11px Arial';
    ctx.fillText('SPRING.', w / 2, 510);

    // Yellow accent bar bottom
    ctx.fillStyle = '#F6ADBC';
    ctx.fillRect(0, h - 6, w, 6);
  };

  const downloadPNG = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 1200;
    canvas.height = 540;
    const ctx = canvas.getContext('2d');
    drawBadge(ctx, 1200, 540);
    const link = document.createElement('a');
    link.download = `SPRING_Stage1_Badge_${candidateName.replace(/\s+/g, '_')}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    toast.success('Badge downloaded');
  };

  const shareLinkedIn = () => {
    const text = encodeURIComponent(
      `I've completed the SPRING Stage 1: Assessment and been assigned to ${pathwayName}.\n\nSPRING is New Zealand's guided platform for career transitioners.\n\n${tagline}\n\n#SPRING #MadeByDoing #NewZealand`
    );
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.origin)}&summary=${text}`, '_blank');
  };

  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/verify/${badgeId}`);
    setCopied(true);
    toast.success('Link copied');
    setTimeout(() => setCopied(false), 3000);
  };

  if (loading || !results) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#94a3b8' }} />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: 'Arial, sans-serif' }}>
      {/* Header */}
      <header style={{ padding: '16px 24px', background: '#fff', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '22px', fontWeight: 800, color: '#1E491A', letterSpacing: '-0.5px' }}>SPRING.</span>
        <span style={{ fontSize: '12px', color: '#94a3b8' }}>Your Badge</span>
      </header>

      <main style={{ maxWidth: '640px', margin: '0 auto', padding: '40px 24px 80px' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <h1 style={{ fontSize: '24px', fontWeight: 800, color: '#1E491A', letterSpacing: '-0.5px', marginBottom: '8px' }} data-testid="badge-heading">
            Stage 1 Complete
          </h1>
          <p style={{ fontSize: '14px', color: '#64748b' }}>
            Your digital completion badge is ready to share.
          </p>
        </div>

        {/* Badge Visual */}
        <div
          style={{
            background: '#1a1a1a', borderRadius: '20px', overflow: 'hidden',
            boxShadow: '0 8px 40px rgba(0,0,0,0.15)', marginBottom: '28px',
          }}
          data-testid="badge-visual"
        >
          {/* Top accent */}
          <div style={{ height: '4px', background: '#F6ADBC' }} />

          <div style={{ padding: '36px 32px', textAlign: 'center' }}>
            {/* Header */}
            <p style={{ fontSize: '24px', fontWeight: 800, color: '#F6ADBC', letterSpacing: '-0.5px', marginBottom: '4px' }}>SPRING.</p>
            <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', letterSpacing: '1px', marginBottom: '24px' }}>STAGE 1: ASSESSMENT COMPLETE</p>

            <div style={{ width: '80%', height: '1px', background: 'rgba(255,255,255,0.1)', margin: '0 auto 24px' }} />

            {/* Name */}
            <p style={{ fontSize: '28px', fontWeight: 800, color: '#fff', letterSpacing: '-0.5px', marginBottom: '6px' }} data-testid="badge-name">
              {candidateName}
            </p>
            <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', marginBottom: '24px' }}>
              has completed the SPRING Stage 1: Assessment
            </p>

            {/* Score */}
            <div style={{
              width: '100px', height: '100px', borderRadius: '50%',
              border: `3px solid ${lc.accent}`, margin: '0 auto 20px',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              background: `${lc.accent}15`,
            }} data-testid="badge-score">
              <span style={{ fontSize: '32px', fontWeight: 800, color: '#fff', lineHeight: 1 }}>
                {results.total_score}
              </span>
              <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>/ 50</span>
            </div>

            {/* Pathway */}
            <p style={{ fontSize: '16px', fontWeight: 700, color: lc.accent, marginBottom: '6px' }} data-testid="badge-pathway">
              Pathway {pathway}: {pathwayName}
            </p>
            <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', fontStyle: 'italic', marginBottom: '24px' }}>
              {tagline}
            </p>

            <div style={{ width: '80%', height: '1px', background: 'rgba(255,255,255,0.1)', margin: '0 auto 20px' }} />

            {/* Footer */}
            <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginBottom: '4px' }}>
              Completed: {completedDate}
            </p>
            <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginBottom: '12px' }}>
              Badge ID: {badgeId}
            </p>
            <p style={{ fontSize: '10px', color: '#F6ADBC', letterSpacing: '0.5px' }}>
              SPRING.
            </p>
          </div>

          {/* Bottom accent */}
          <div style={{ height: '4px', background: '#F6ADBC' }} />
        </div>

        {/* Share Actions */}
        <div style={{
          background: '#fff', borderRadius: 0, padding: '20px',
          border: '1px solid #e2e8f0', marginBottom: '24px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <Share2 style={{ width: 14, height: 14, color: '#94a3b8' }} />
            <span style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94a3b8' }}>
              Share Your Achievement
            </span>
          </div>

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              onClick={downloadPNG}
              data-testid="badge-download-btn"
              style={{
                flex: 1, minWidth: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                padding: '12px 16px', borderRadius: 0,
                background: '#1a1a1a', color: '#F6ADBC', border: 'none',
                fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              <Download style={{ width: 14, height: 14 }} />
              Download PNG
            </button>

            <button
              onClick={shareLinkedIn}
              data-testid="badge-linkedin-btn"
              style={{
                flex: 1, minWidth: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                padding: '12px 16px', borderRadius: 0,
                background: '#0077B5', color: '#fff', border: 'none',
                fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              <Linkedin style={{ width: 14, height: 14 }} />
              Share to LinkedIn
            </button>

            <button
              onClick={copyLink}
              data-testid="badge-copy-link-btn"
              style={{
                flex: 1, minWidth: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                padding: '12px 16px', borderRadius: 0,
                background: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0',
                fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {copied ? <Check style={{ width: 14, height: 14, color: '#22c55e' }} /> : <Link2 style={{ width: 14, height: 14 }} />}
              {copied ? 'Copied!' : 'Copy Link'}
            </button>
          </div>
        </div>

        {/* Continue */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={() => navigate('/portal')}
            data-testid="badge-continue-btn"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '10px',
              background: '#1a1a1a', color: '#F6ADBC', border: 'none',
              padding: '16px 36px', borderRadius: 0,
              fontSize: '16px', fontWeight: 700, cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={(e) => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 8px 24px rgba(0,0,0,0.2)'; }}
            onMouseLeave={(e) => { e.target.style.transform = ''; e.target.style.boxShadow = ''; }}
          >
            Go to Your Springboard
            <ArrowRight style={{ width: 18, height: 18 }} />
          </button>
        </div>
      </main>
    </div>
  );
};

export default Badge;
